import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronLeft, ChevronRight, UserPlus, Trash2, Plus, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtVisitDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}
const STATUSES = [
  { key: "confirmed", label: "Confirmed",   bg: "#D8ECD9", text: "#1F4E2C" },
  { key: "booked",    label: "Booked",      bg: "#2D7A4F", text: "#FFFFFF" },
  { key: "tentative", label: "Tentative",   bg: "#FDE8B4", text: "#8A5E00" },
  { key: "hold",      label: "On Hold",     bg: "#FBD5D5", text: "#8B1E1E" },
  { key: "na",        label: "N/A",         bg: "#DDDDDD", text: "#555555" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

export default function ServiceWork() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const activeOrgName = (user?.organization_name || "").replace(/[–—]/g, "-").toLowerCase().trim();
  const isRheelInternal = activeOrgName === "rheel group - internal";
  const [tab, setTab] = useState("calendar");
  const [year, setYear] = useState(new Date().getFullYear());
  const [engineers, setEngineers] = useState([]);
  const [clients, setClients] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);
  const [cell, setCell] = useState(null); // { client, month, existing? }

  const load = async () => {
    setLoading(true);
    try {
      const [e, c, v] = await Promise.all([
        api.get("/service-work/engineers"),
        api.get("/service-work/clients"),
        api.get(`/service-work/visits?year=${year}`),
      ]);
      setEngineers(e.data);
      setClients(c.data);
      setVisits(v.data);
    } catch { toast.error("Failed to load service work data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const visitMap = useMemo(() => {
    const m = {};
    visits.forEach((v) => { m[`${v.client_name}|${v.month}`] = v; });
    return m;
  }, [visits]);

  const engineerById = useMemo(() => Object.fromEntries(engineers.map((e) => [e.id, e])), [engineers]);

  const openCell = (clientName, month) => {
    const existing = visitMap[`${clientName}|${month}`];
    setCell({ client: clientName, month, existing });
  };

  const saveCell = async (payload) => {
    try {
      await api.put("/service-work/visits", { ...payload, year });
      toast.success("Saved");
      setCell(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const clearCell = async () => {
    if (!cell) return;
    try {
      await api.delete(`/service-work/visits?client_name=${encodeURIComponent(cell.client)}&year=${year}&month=${cell.month}`);
      toast.success("Cleared");
      setCell(null);
      load();
    } catch { toast.error("Failed to clear"); }
  };

  if (!isRheelInternal) {
    return (
      <div data-testid="service-work-page">
        <PageHeader
          eyebrow="[11] Service Work"
          title="Monthly Service Schedule"
          subtitle="Available only from the Rheel Group – Internal organization."
        />
        <div className="p-4 sm:p-6 lg:p-8">
          <div data-testid="service-work-restricted" className="border border-[#E5E0D5] bg-white p-8 max-w-xl">
            <div className="uppercase-label text-[#B45309] mb-2">Restricted view</div>
            <p className="text-sm text-[#3A3A3A]">
              The monthly service schedule is Rheel&apos;s internal roster and is only visible when the
              <strong> Rheel Group – Internal</strong> organization is selected in the sidebar.
              Switch organizations to access it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="service-work-page">
      <PageHeader
        eyebrow="[11] Service Work"
        title="Monthly Service Schedule"
        subtitle="Assign an engineer per client per month. Colour reflects booking status."
        actions={
          <>
            <div className="flex items-center gap-2 mr-3" data-testid="year-picker">
              <button onClick={() => setYear(year - 1)} className="p-1.5 border border-[#D6CFC1] hover:bg-[#F0EDE3]" data-testid="year-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
              <span className="font-mono text-sm text-[#0F0F0F] w-14 text-center">{year}</span>
              <button onClick={() => setYear(year + 1)} className="p-1.5 border border-[#D6CFC1] hover:bg-[#F0EDE3]" data-testid="year-next"><ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
            {isAdmin && (
              <Button
                data-testid="manage-engineers-btn"
                variant="outline"
                onClick={() => setManageOpen(true)}
                className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase"
              >
                <UserPlus className="w-3.5 h-3.5 mr-2" /> Engineers & Clients
              </Button>
            )}
          </>
        }
      />

      <div className="px-4 sm:px-6 lg:px-8 pt-2">
        <div className="flex border-b border-[#E5E0D5] gap-6">
          {[["calendar", "Calendar"], ["forms", "Forms"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`tab-${k}`}
              onClick={() => setTab(k)}
              className={`py-2 -mb-px font-mono text-xs uppercase tracking-wider border-b-2 ${tab === k ? "border-[#F37B2A] text-[#0F0F0F]" : "border-transparent text-[#7A7A7A] hover:text-[#0F0F0F]"}`}
            >{label}</button>
          ))}
        </div>
      </div>

      {tab === "calendar" && (
        <div className="p-4 sm:p-6 lg:p-8 space-y-4">
          {/* Legend */}
          <div className="flex flex-wrap gap-3 items-center">
            {STATUSES.map((s) => (
              <div key={s.key} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#5A5A5A]">
                <span className="inline-block w-3 h-3 border border-[#0000001a]" style={{ background: s.bg }} />
                {s.label}
              </div>
            ))}
            {engineers.filter((e) => e.active).map((e) => (
              <div key={e.id} className="flex items-center gap-1.5 font-mono text-[10px] text-[#5A5A5A]">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: e.color }} />
                {e.name}
              </div>
            ))}
          </div>

          <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="calendar-table">
              <thead>
                <tr className="border-b border-[#E5E0D5] bg-[#FAF7F0]">
                  <th className="sticky left-0 bg-[#FAF7F0] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] text-left border-r border-[#E5E0D5]">Client</th>
                  {MONTHS.map((m, i) => (
                    <th key={m} className="px-2 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] text-center" data-testid={`col-month-${i + 1}`}>{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={13} className="px-4 py-10 text-center font-mono text-xs text-[#7A7A7A]">Loading…</td></tr>}
                {!loading && clients.length === 0 && (
                  <tr><td colSpan={13} className="px-4 py-10 text-center font-mono text-xs text-[#7A7A7A]">
                    No clients yet. Use <strong>Engineers & Clients</strong> to add some, or add organizations from the Admin page.
                  </td></tr>
                )}
                {clients.map((client) => (
                  <tr key={client.name} data-testid={`row-${client.name}`} className="border-b border-[#E5E0D5]">
                    <td className="sticky left-0 bg-white px-4 py-2 border-r border-[#E5E0D5] text-[#0F0F0F] whitespace-nowrap">
                      {client.name}
                      {client.source === "custom" && <span className="ml-2 font-mono text-[9px] uppercase tracking-wider text-[#9A9A9A]">custom</span>}
                    </td>
                    {MONTHS.map((_, i) => {
                      const month = i + 1;
                      const visit = visitMap[`${client.name}|${month}`];
                      const eng = visit && engineerById[visit.engineer_id];
                      const st = visit ? STATUS_MAP[visit.status] : null;
                      return (
                        <td
                          key={month}
                          data-testid={`cell-${client.name}-${month}`}
                          onClick={() => openCell(client.name, month)}
                          style={visit ? { background: st.bg, color: st.text } : {}}
                          className="px-2 py-2 text-center border-l border-[#F0EDE3] font-mono text-xs cursor-pointer hover:outline hover:outline-1 hover:outline-[#F37B2A] transition-all"
                          title={visit ? `${eng?.name || "—"} · ${st?.label || ""}${visit.visit_date ? ` · ${fmtVisitDate(visit.visit_date)}` : ""}${visit.notes ? ` · ${visit.notes}` : ""}` : "Click to assign"}
                        >
                          {visit ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <div className="flex items-center justify-center gap-1.5">
                                {eng && <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: eng.color }} />}
                                <span>{eng?.name || "—"}</span>
                              </div>
                              {visit.visit_date && (
                                <span
                                  data-testid={`cell-date-${client.name}-${month}`}
                                  className="block text-[10px] leading-tight font-semibold"
                                >
                                  {fmtVisitDate(visit.visit_date)}
                                </span>
                              )}
                              {visit.notes?.trim() && (
                                <span
                                  data-testid={`cell-note-${client.name}-${month}`}
                                  className="block max-w-[110px] truncate text-[10px] leading-tight opacity-75 normal-case"
                                >
                                  {visit.notes.trim().split("\n")[0]}
                                </span>
                              )}
                            </div>
                          ) : <span className="text-[#D6CFC1]">·</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#9A9A9A]">
            Click any cell to assign, change status, or clear.
          </div>
        </div>
      )}

      {tab === "forms" && (
        <div className="p-4 sm:p-6 lg:p-8 space-y-4">
          <div className="border border-[#E5E0D5] bg-white p-6" data-testid="service-form-monthly-health">
            <div className="flex items-start gap-3">
              <ClipboardList className="w-5 h-5 text-[#F37B2A] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
              <div className="flex-1">
                <div className="font-medium text-[#0F0F0F]">Monthly Systems Health Check</div>
                <p className="text-sm text-[#5A5A5A] mt-1 max-w-2xl">
                  Full monthly review across M365, ESET, UniFi, servers, backups, NAS, physical infrastructure, workstations and the client summary report. Complete each month per client — remote or onsite — and sign off to lock as an audit record.
                </p>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Link
                    to="/forms/new/monthly-systems-health-check"
                    data-testid="start-monthly-health"
                    className="inline-flex items-center gap-2 rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase h-10 px-4"
                  >Start form</Link>
                  <Link
                    to="/forms"
                    className="inline-flex items-center gap-2 rounded-none border border-[#D6CFC1] bg-transparent hover:bg-[#F0EDE3] text-[#3A3A3A] font-mono text-xs tracking-wider uppercase h-10 px-4"
                  >All forms & audit history</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cell editor */}
      {cell && (
        <CellEditor
          cell={cell}
          engineers={engineers}
          onClose={() => setCell(null)}
          onSave={saveCell}
          onClear={clearCell}
          year={year}
        />
      )}

      {/* Manage engineers & clients */}
      {manageOpen && (
        <ManageDialog
          onClose={() => setManageOpen(false)}
          engineers={engineers}
          clients={clients}
          onReload={load}
        />
      )}
    </div>
  );
}

function CellEditor({ cell, engineers, onClose, onSave, onClear, year }) {
  const existing = cell.existing;
  const [engineerId, setEngineerId] = useState(existing?.engineer_id || "");
  const [status, setStatus] = useState(existing?.status || "confirmed");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [visitDate, setVisitDate] = useState(existing?.visit_date || "");
  const mm = String(cell.month).padStart(2, "0");
  const daysInMonth = new Date(year, cell.month, 0).getDate();
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-medium tracking-tight">
            {cell.client} — {MONTHS[cell.month - 1]} {year}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#5A5A5A] text-sm">
            Assign an engineer and set the booking status for this visit.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="uppercase-label">Engineer</Label>
            <Select value={engineerId || "none"} onValueChange={(v) => setEngineerId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="cell-engineer" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
                <SelectItem value="none">— unassigned —</SelectItem>
                {engineers.filter((e) => e.active).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="uppercase-label">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="cell-status" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
                {STATUSES.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="uppercase-label">Visit Date</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="date"
                data-testid="cell-visit-date"
                value={visitDate}
                min={`${year}-${mm}-01`}
                max={`${year}-${mm}-${String(daysInMonth).padStart(2, "0")}`}
                onChange={(e) => setVisitDate(e.target.value)}
                className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm flex-1"
              />
              {visitDate && (
                <button
                  type="button"
                  data-testid="cell-visit-date-clear"
                  onClick={() => setVisitDate("")}
                  className="font-mono text-[10px] uppercase tracking-wider text-[#7A7A7A] hover:text-[#B91C1C] px-2"
                >Clear</button>
              )}
            </div>
          </div>
          <div>
            <Label className="uppercase-label">Notes</Label>
            <Textarea
              data-testid="cell-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. booked 27th, morning only"
              className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
            />
          </div>
        </div>
        <AlertDialogFooter>
          {existing && (
            <button
              onClick={onClear}
              data-testid="cell-clear"
              className="mr-auto font-mono text-xs uppercase tracking-wider text-[#B91C1C] hover:text-[#800] px-3"
            >Clear</button>
          )}
          <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="cell-save"
            onClick={(e) => { e.preventDefault(); onSave({ client_name: cell.client, month: cell.month, engineer_id: engineerId || null, status, notes, visit_date: visitDate || null }); }}
            className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white"
          >Save</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ManageDialog({ onClose, engineers, clients, onReload }) {
  const [tab, setTab] = useState("engineers");
  const [newEng, setNewEng] = useState({ name: "", color: "" });
  const [newClient, setNewClient] = useState("");

  const addEng = async () => {
    if (!newEng.name.trim()) return;
    try { await api.post("/service-work/engineers", newEng); setNewEng({ name: "", color: "" }); onReload(); toast.success("Engineer added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateEng = async (id, patch) => {
    try { await api.put(`/service-work/engineers/${id}`, patch); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteEng = async (id) => {
    if (!window.confirm("Delete this engineer? Any assigned visits will become unassigned.")) return;
    try { await api.delete(`/service-work/engineers/${id}`); onReload(); toast.success("Engineer removed"); }
    catch { toast.error("Failed"); }
  };
  const addClient = async () => {
    if (!newClient.trim()) return;
    try { await api.post("/service-work/clients", { name: newClient.trim() }); setNewClient(""); onReload(); toast.success("Client added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteClient = async (id) => {
    if (!window.confirm("Remove this custom client?")) return;
    try { await api.delete(`/service-work/clients/${id}`); onReload(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };
  const customClients = clients.filter((c) => c.source === "custom");

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-medium tracking-tight">Engineers & Clients</AlertDialogTitle>
          <AlertDialogDescription className="text-[#5A5A5A] text-sm">
            Manage the engineer list (with colours) and any extra clients that aren&apos;t in the organizations list.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex border-b border-[#E5E0D5] gap-6 mb-4">
          {[["engineers", "Engineers"], ["clients", "Custom Clients"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`py-2 -mb-px font-mono text-xs uppercase tracking-wider border-b-2 ${tab === k ? "border-[#F37B2A] text-[#0F0F0F]" : "border-transparent text-[#7A7A7A] hover:text-[#0F0F0F]"}`}
            >{label}</button>
          ))}
        </div>

        {tab === "engineers" && (
          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_120px_auto] gap-2">
              <Input data-testid="new-engineer-name" value={newEng.name} onChange={(e) => setNewEng({ ...newEng, name: e.target.value })} placeholder="Engineer name" className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
              <Input type="color" value={newEng.color || "#F37B2A"} onChange={(e) => setNewEng({ ...newEng, color: e.target.value })} className="rounded-none border-[#E5E0D5] p-1 h-10" />
              <Button data-testid="add-engineer-btn" onClick={addEng} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="border border-[#E5E0D5] max-h-80 overflow-auto">
              {engineers.length === 0 && <div className="px-4 py-6 font-mono text-xs text-[#9A9A9A] text-center">No engineers yet.</div>}
              {engineers.map((e) => (
                <div key={e.id} className="flex items-center gap-3 px-3 py-2 border-b border-[#F0EDE3] last:border-b-0">
                  <input type="color" value={e.color || "#F37B2A"} onChange={(ev) => updateEng(e.id, { name: e.name, color: ev.target.value, active: e.active })} className="w-8 h-8 border border-[#E5E0D5] p-0.5" />
                  <span className="flex-1 font-mono text-sm">{e.name}</span>
                  <label className="flex items-center gap-1 font-mono text-[10px] uppercase text-[#5A5A5A] cursor-pointer">
                    <input type="checkbox" checked={e.active} onChange={(ev) => updateEng(e.id, { name: e.name, color: e.color, active: ev.target.checked })} className="accent-[#F37B2A]" />
                    Active
                  </label>
                  <button onClick={() => deleteEng(e.id)} data-testid={`del-eng-${e.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "clients" && (
          <div className="space-y-3">
            <p className="text-xs text-[#7A7A7A]">Organizations from Admin appear on the calendar automatically. Use this list to add clients who aren&apos;t formal organizations in the system.</p>
            <div className="flex gap-2">
              <Input data-testid="new-client-name" value={newClient} onChange={(e) => setNewClient(e.target.value)} placeholder="Client name" className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
              <Button data-testid="add-client-btn" onClick={addClient} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="border border-[#E5E0D5] max-h-80 overflow-auto">
              {customClients.length === 0 && <div className="px-4 py-6 font-mono text-xs text-[#9A9A9A] text-center">No custom clients yet.</div>}
              {customClients.map((c) => (
                <div key={c.id} className="flex items-center gap-3 px-3 py-2 border-b border-[#F0EDE3] last:border-b-0">
                  <span className="flex-1 font-mono text-sm">{c.name}</span>
                  <button onClick={() => deleteClient(c.id)} data-testid={`del-client-${c.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose} className="rounded-none bg-[#1A1A1A] hover:bg-[#000000] text-white">Done</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
